import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Seal from "@/components/Seal";
import BrandBanner from "@/components/BrandBanner";
import { ReviewTimeline } from "@/components/ReviewTimeline";
import {
  ShieldCheck, FileSearch, Users, Leaf, Scale, Boxes, Layers, Cpu,
  ArrowRight, CheckCircle2, Sparkles, BadgeCheck,
} from "lucide-react";

const categories = [
  { icon: Users, name: "Labor & Human Rights", desc: "Child & forced labor prevention, working hours, wages, non-discrimination, grievance." },
  { icon: Leaf, name: "EHS & Environment", desc: "Occupational safety, chemical & waste management, permits, energy & GHG." },
  { icon: Scale, name: "Business Ethics & Anti-Corruption", desc: "FCPA-style anti-bribery, gifts controls, conflicts of interest, whistleblowing." },
  { icon: Boxes, name: "Responsible Procurement & Supplier DD", desc: "Screening, beneficial ownership, sanctions/PEP, ongoing monitoring." },
  { icon: Layers, name: "Management System & Improvement", desc: "Training, risk assessment, internal review, corrective actions." },
  { icon: Cpu, name: "AI Governance, Security & Responsible Use", desc: "AI governance, data privacy, LLM security, human oversight, bias, vendor & incident management." },
];

const steps = [
  { n: 1, t: "Apply", d: "Submit your company profile and requested certification level." },
  { n: 2, t: "Questionnaire", d: "Answer topic-by-topic questions across your selected categories." },
  { n: 3, t: "Upload Documents", d: "Provide policies, procedures, and records as evidence." },
  { n: 4, t: "Submit & Pay", d: "Pay securely by card or ACH — payment confirmation starts the review." },
  { n: 5, t: "Review & Certificate", d: "Our reviewers verify documents and issue your certificate." },
];

const levels = [
  { name: "Silver", range: "70–79", color: "#8A94A6" },
  { name: "Gold", range: "80–89", color: "#D6A84A" },
  { name: "Platinum", range: "90–100", color: "#2B3A55" },
];

const Home = () => (
  <div>
    {/* Logo banner */}
    <div className="wechr-container pt-6">
      <BrandBanner data-testid="home-logo-banner" />
    </div>

    {/* Hero */}
    <section className="hero-wash">
      <div className="wechr-container grid gap-10 py-16 lg:grid-cols-2 lg:py-24">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-[var(--wechr-border)] bg-white px-3 py-1 text-xs font-medium text-wgreen-700">
            <Sparkles className="h-3.5 w-3.5" /> Document-reviewed certification
          </div>
          <h1 className="mt-4 font-heading text-4xl font-semibold tracking-tight text-navy-900 sm:text-5xl">
            Demonstrate responsible business practices, credibly.
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-navy-800">
            WECH-RB helps companies demonstrate responsible business practices through a document-reviewed
            certification process covering EHS, ESG, ethics, anti-corruption, responsible procurement,
            supplier due diligence, and AI governance, security &amp; responsible use.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/apply"><Button size="lg" className="btn-green" data-testid="hero-apply-button">Apply for certification <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            <Link to="/verify"><Button size="lg" variant="outline" data-testid="hero-verify-button">Verify a certificate</Button></Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[var(--wechr-muted-text)]">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-wgreen-600" /> AI-assisted gap analysis</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-wgreen-600" /> Human admin review</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-wgreen-600" /> Public verification</span>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }} className="flex items-center justify-center">
          <Card className="w-full max-w-md shadow-[0_20px_50px_rgba(11,27,51,0.10)]">
            <CardContent className="p-8">
              <div className="flex items-center gap-3">
                <Seal size={44} />
                <div>
                  <p className="font-heading text-lg font-bold text-navy-900">WECH-RB DD Certification</p>
                  <p className="text-xs text-[var(--wechr-muted-text)]">Document-Reviewed EHS, ESG, Ethics, Supplier Due Diligence &amp; AI Governance</p>
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2">
                {levels.map((l) => (
                  <div key={l.name} className="rounded-lg border border-[var(--wechr-border)] p-2 text-center">
                    <div className="mx-auto mb-1 h-6 w-6 rounded-full" style={{ background: l.color }} />
                    <p className="text-xs font-semibold text-navy-900">{l.name}</p>
                    <p className="text-[10px] text-[var(--wechr-muted-text)]">{l.range}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-2 text-sm text-navy-800">
                {["6 assessment categories, weighted equally (1/6 each)", "Evidence-based scoring", "Verifiable certificate with QR code"].map((t) => (
                  <p key={t} className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-wgreen-600" /> {t}</p>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>

    {/* Brand statement */}
    <section className="wechr-section">
      <div className="wechr-container">
        <div className="rounded-2xl border border-[var(--wechr-border)] bg-[var(--wechr-surface)] p-8 sm:p-12">
          <ShieldCheck className="h-8 w-8 text-wgreen-600" />
          <p className="mt-4 max-w-4xl font-heading text-xl font-medium leading-relaxed text-navy-900 sm:text-2xl">
            WECH-RB stands for We Create Harmony with Responsible Businesses. We believe responsible companies
            committed to EHS, ESG, ethics, labor responsibility, anti-corruption, responsible procurement,
            supplier due diligence, and responsible AI governance can help create a safer, more sustainable, and more harmonious society.
          </p>
        </div>
      </div>
    </section>

    {/* Categories */}
    <section className="wechr-section bg-[var(--wechr-surface)]">
      <div className="wechr-container">
        <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy-900">Six assessment categories</h2>
        <p className="mt-2 text-[var(--wechr-muted-text)]">Each category is weighted equally — one-sixth (about 16.7%) of your overall score for full certification. For category-based certification, the weighting is split equally across the categories you choose.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <motion.div key={c.name} whileHover={{ y: -2 }} transition={{ duration: 0.18 }}>
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-wgreen-100"><c.icon className="h-5 w-5 text-wgreen-700" /></div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-navy-900">{c.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--wechr-muted-text)]">{c.desc}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Process */}
    <section className="wechr-section">
      <div className="wechr-container">
        <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy-900">How certification works</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-5">
          {steps.map((s) => (
            <div key={s.n} className="relative rounded-xl border border-[var(--wechr-border)] bg-white p-5">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-navy-900 font-heading text-sm font-semibold text-white">{s.n}</div>
              <h3 className="mt-3 font-heading text-sm font-semibold text-navy-900">{s.t}</h3>
              <p className="mt-1 text-sm text-[var(--wechr-muted-text)]">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8"><Link to="/certification-process"><Button variant="outline">See full process <ArrowRight className="ml-1 h-4 w-4" /></Button></Link></div>
      </div>
    </section>

    {/* Estimated Review Timeline */}
    <section className="wechr-section bg-[var(--wechr-surface)]">
      <div className="wechr-container">
        <ReviewTimeline />
      </div>
    </section>

    {/* CTA */}
    <section className="wechr-section">
      <div className="wechr-container">
        <div className="flex flex-col items-center justify-between gap-6 rounded-2xl bg-navy-900 p-10 text-center sm:flex-row sm:text-left">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-white">Ready to demonstrate responsible business?</h2>
            <p className="mt-2 text-sm text-[#B9C4D6]">Start your document-reviewed WECH-RB DD Certification today.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/apply"><Button size="lg" className="btn-green">Apply now</Button></Link>
            <Link to="/pricing"><Button size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10">View pricing</Button></Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Home;
